import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Link,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {
  fetchTask,
  deleteTask,
  fetchDataTypes,
  addTaskDataType,
  deleteTaskDataType,
  fetchGuidelines,
  createGuideline,
  updateGuideline,
  deleteGuideline,
  fetchAgents,
} from "../../api/companyApi";
import { COMPANY_ID } from "../../config/appConfig";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function TaskDetailPage() {
  const { businessDomainId, jobId, taskId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── DataType state ──
  const [availableDataTypes, setAvailableDataTypes] = useState([]);
  const [dtDialogOpen, setDtDialogOpen] = useState(false);
  const [dtForm, setDtForm] = useState({ dataTypeId: "", dataAccessType: "" });
  const [dtConfirmOpen, setDtConfirmOpen] = useState(false);
  const [dtDeleteTarget, setDtDeleteTarget] = useState(null);

  // ── Guideline state ──
  const [guidelines, setGuidelines] = useState([]);
  const [glDialogOpen, setGlDialogOpen] = useState(false);
  const [glEditMode, setGlEditMode] = useState(false);
  const [glEditData, setGlEditData] = useState(null);
  const [glForm, setGlForm] = useState({ guidelineName: "", guidelineDescription: "", url: "" });
  const [glConfirmOpen, setGlConfirmOpen] = useState(false);
  const [glDeleteTarget, setGlDeleteTarget] = useState(null);

  // ── Agent state ──
  const [agents, setAgents] = useState([]);
  const [agDialogOpen, setAgDialogOpen] = useState(false);
  const [agEditMode, setAgEditMode] = useState(false);
  const [agEditData, setAgEditData] = useState(null);
  const [agForm, setAgForm] = useState({ guidelineName: "", guidelineDescription: "", url: "" });
  const [agConfirmOpen, setAgConfirmOpen] = useState(false);
  const [agDeleteTarget, setAgDeleteTarget] = useState(null);

  // ── Loaders ──
  const loadData = useCallback(async () => {
    try { setData(await fetchTask(taskId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const loadGuidelines = useCallback(async () => {
    try { setGuidelines(await fetchGuidelines(taskId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const loadAgents = useCallback(async () => {
    try { setAgents(await fetchAgents(taskId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadGuidelines(); }, [loadGuidelines]);
  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => {
    (async () => {
      try { setAvailableDataTypes(await fetchDataTypes(businessDomainId)); }
      catch (e) { /* ignore */ }
    })();
  }, [businessDomainId]);

  // ── DataType CRUD ──
  const openDtAdd = () => {
    setDtForm({ dataTypeId: "", dataAccessType: "" });
    setDtDialogOpen(true);
  };

  const handleDtSave = async () => {
    if (!dtForm.dataTypeId) { notify.error(t("message.error.required")); return; }
    if (!dtForm.dataAccessType) { notify.error(t("message.error.required")); return; }
    const selectedDt = availableDataTypes.find((d) => d.dataTypeId === dtForm.dataTypeId);
    const payload = {
      dataTypeId: dtForm.dataTypeId,
      dataTypeName: selectedDt?.dataTypeName || "",
      dataAccessType: dtForm.dataAccessType,
    };
    try {
      await addTaskDataType(taskId, payload);
      notify.success(t("message.create.success"));
      setDtDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };

  const handleDtDelete = async () => {
    try {
      await deleteTaskDataType(taskId, dtDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setDtConfirmOpen(false);
    setDtDeleteTarget(null);
  };

  // ── Guideline CRUD ──
  const openGlAdd = () => {
    setGlEditMode(false);
    setGlEditData(null);
    setGlForm({ guidelineName: "", guidelineDescription: "", url: "" });
    setGlDialogOpen(true);
  };

  const openGlEdit = (row) => {
    setGlEditMode(true);
    setGlEditData(row);
    setGlForm({
      guidelineName: row.guidelineName || "",
      guidelineDescription: row.guidelineDescription || "",
      url: row.url || "",
    });
    setGlDialogOpen(true);
  };

  const handleGlSave = async () => {
    if (!glForm.guidelineName) { notify.error(t("message.error.required")); return; }
    if (!glForm.guidelineDescription) { notify.error(t("message.error.required")); return; }
    try {
      if (glEditMode) {
        await updateGuideline({
          ...glEditData,
          companyId: COMPANY_ID,
          targetId: taskId,
          guidelineTarget: "Task",
          guidelineName: glForm.guidelineName,
          guidelineDescription: glForm.guidelineDescription,
          url: glForm.url,
        });
        notify.success(t("message.update.success"));
      } else {
        await createGuideline({
          companyId: COMPANY_ID,
          targetId: taskId,
          guidelineTarget: "Task",
          guidelineName: glForm.guidelineName,
          guidelineDescription: glForm.guidelineDescription,
          url: glForm.url,
        });
        notify.success(t("message.create.success"));
      }
      setGlDialogOpen(false);
      loadGuidelines();
    } catch (e) { notify.apiError(e); }
  };

  const handleGlDelete = async () => {
    try {
      await deleteGuideline(glDeleteTarget);
      notify.success(t("message.delete.success"));
      loadGuidelines();
    } catch (e) { notify.apiError(e); }
    setGlConfirmOpen(false);
    setGlDeleteTarget(null);
  };

  // ── Agent CRUD ──
  const openAgAdd = () => {
    setAgEditMode(false);
    setAgEditData(null);
    setAgForm({ guidelineName: "", guidelineDescription: "", url: "" });
    setAgDialogOpen(true);
  };

  const openAgEdit = (row) => {
    setAgEditMode(true);
    setAgEditData(row);
    setAgForm({
      guidelineName: row.guidelineName || "",
      guidelineDescription: row.guidelineDescription || "",
      url: row.url || "",
    });
    setAgDialogOpen(true);
  };

  const handleAgSave = async () => {
    if (!agForm.guidelineName) { notify.error(t("message.error.required")); return; }
    if (!agForm.guidelineDescription) { notify.error(t("message.error.required")); return; }
    try {
      if (agEditMode) {
        await updateGuideline({
          ...agEditData,
          companyId: COMPANY_ID,
          targetId: taskId,
          guidelineTarget: "Agent",
          guidelineName: agForm.guidelineName,
          guidelineDescription: agForm.guidelineDescription,
          url: agForm.url,
        });
        notify.success(t("message.update.success"));
      } else {
        await createGuideline({
          companyId: COMPANY_ID,
          targetId: taskId,
          guidelineTarget: "Agent",
          guidelineName: agForm.guidelineName,
          guidelineDescription: agForm.guidelineDescription,
          url: agForm.url,
        });
        notify.success(t("message.create.success"));
      }
      setAgDialogOpen(false);
      loadAgents();
    } catch (e) { notify.apiError(e); }
  };

  const handleAgDelete = async () => {
    try {
      await deleteGuideline(agDeleteTarget);
      notify.success(t("message.delete.success"));
      loadAgents();
    } catch (e) { notify.apiError(e); }
    setAgConfirmOpen(false);
    setAgDeleteTarget(null);
  };

  const handleDelete = async () => {
    try {
      await deleteTask(taskId);
      notify.success(t("message.delete.success"));
      navigate(`/design/${businessDomainId}/jobs/${jobId}`);
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const associatedDataTypes = data.associatedDataTypes || [];

  // ── Column definitions ──
  const dtColumns = [
    { field: "dataTypeName", headerName: t("taskDataType.dataType"), flex: 1, minWidth: 180 },
    {
      field: "dataAccessType",
      headerName: t("taskDataType.accessType"),
      width: 160,
      valueFormatter: (value) => {
        const key = `taskDataType.access.${value}`;
        return intl.formatMessage({ id: key, defaultMessage: value });
      },
    },
    {
      field: "actions", headerName: t("common.actions"), width: 80, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => {
          e.stopPropagation();
          setDtDeleteTarget(p.row);
          setDtConfirmOpen(true);
        }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const glColumns = [
    { field: "guidelineName", headerName: t("guideline.name"), flex: 1, minWidth: 180 },
    { field: "guidelineDescription", headerName: t("guideline.description"), flex: 1, minWidth: 180, renderCell: wrapTextCell },
    {
      field: "url", headerName: t("guideline.url"), width: 160,
      renderCell: (p) => p.value ? (
        <Link href={p.value} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          {p.value}
        </Link>
      ) : null,
    },
    {
      field: "actions", headerName: t("common.actions"), width: 110, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openGlEdit(p.row); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => {
            e.stopPropagation();
            setGlDeleteTarget(p.row.guidelineId);
            setGlConfirmOpen(true);
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const agColumns = [
    { field: "guidelineName", headerName: t("agent.name"), flex: 1, minWidth: 180 },
    { field: "guidelineDescription", headerName: t("agent.description"), flex: 1, minWidth: 180, renderCell: wrapTextCell },
    {
      field: "url", headerName: t("agent.url"), width: 160,
      renderCell: (p) => p.value ? (
        <Link href={p.value} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          {p.value}
        </Link>
      ) : null,
    },
    {
      field: "actions", headerName: t("common.actions"), width: 110, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openAgEdit(p.row); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => {
            e.stopPropagation();
            setAgDeleteTarget(p.row.guidelineId);
            setAgConfirmOpen(true);
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("task.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}/tasks/${taskId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("task.name")} value={data.taskName} /></Grid>
          <Grid size={12}><DetailField label={t("task.description")} value={data.taskDescription} /></Grid>
          <Grid size={12}><DetailField label={t("task.function")} value={data.functionName} /></Grid>
          {data.url && (
            <Grid size={12}>
              <DetailField
                label={t("common.url")}
                value={
                  <Link href={data.url} target="_blank" rel="noopener noreferrer">
                    {data.url}
                  </Link>
                }
              />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label={t("taskDataType.title")} />
          <Tab label={t("guideline.title")} />
          <Tab label={t("agent.title")} />
        </Tabs>
      </Box>

      {/* Tab 0: DataType */}
      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("taskDataType.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openDtAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={associatedDataTypes}
          columns={dtColumns}
          getRowId={(r) => r.dataTypeId}
          key={associatedDataTypes.map((d) => d.dataTypeId).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
        />
      </TabPanel>

      {/* Tab 1: Guideline */}
      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("guideline.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openGlAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={guidelines}
          columns={glColumns}
          getRowId={(r) => r.guidelineId}
          key={guidelines.map((g) => g.guidelineId).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
        />
      </TabPanel>

      {/* Tab 2: Agent */}
      <TabPanel value={tabIndex} index={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("agent.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAgAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={agents}
          columns={agColumns}
          getRowId={(r) => r.guidelineId}
          key={agents.map((a) => a.guidelineId).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
        />
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}`)}>{t("common.back")}</Button>
      </Box>

      {/* Confirm Dialogs */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={dtConfirmOpen} onConfirm={handleDtDelete} onCancel={() => { setDtConfirmOpen(false); setDtDeleteTarget(null); }} />
      <ConfirmDialog open={glConfirmOpen} onConfirm={handleGlDelete} onCancel={() => { setGlConfirmOpen(false); setGlDeleteTarget(null); }} />
      <ConfirmDialog open={agConfirmOpen} onConfirm={handleAgDelete} onCancel={() => { setAgConfirmOpen(false); setAgDeleteTarget(null); }} />

      {/* DataType Add Dialog */}
      <Dialog open={dtDialogOpen} onClose={() => setDtDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("taskDataType.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>{t("taskDataType.dataType")}</InputLabel>
              <Select
                value={dtForm.dataTypeId}
                onChange={(e) => setDtForm({ ...dtForm, dataTypeId: e.target.value })}
                label={t("taskDataType.dataType")}
              >
                {availableDataTypes.map((dt) => (
                  <MenuItem key={dt.dataTypeId} value={dt.dataTypeId}>{dt.dataTypeName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{t("taskDataType.accessType")}</InputLabel>
              <Select
                value={dtForm.dataAccessType}
                onChange={(e) => setDtForm({ ...dtForm, dataAccessType: e.target.value })}
                label={t("taskDataType.accessType")}
              >
                {["Create", "Read", "Update", "Delete"].map((access) => (
                  <MenuItem key={access} value={access}>
                    {t(`taskDataType.access.${access}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDtDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleDtSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>

      {/* Guideline Add/Edit Dialog */}
      <Dialog open={glDialogOpen} onClose={() => setGlDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{glEditMode ? t("guideline.edit.title") : t("guideline.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              fullWidth required
              label={t("guideline.name")}
              value={glForm.guidelineName}
              onChange={(e) => setGlForm({ ...glForm, guidelineName: e.target.value })}
            />
            <TextField
              fullWidth required multiline rows={3}
              label={t("guideline.description")}
              value={glForm.guidelineDescription}
              onChange={(e) => setGlForm({ ...glForm, guidelineDescription: e.target.value })}
            />
            <TextField
              fullWidth
              label={t("guideline.url")}
              value={glForm.url}
              onChange={(e) => setGlForm({ ...glForm, url: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleGlSave}>
            {glEditMode ? t("common.save") : t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agent Add/Edit Dialog */}
      <Dialog open={agDialogOpen} onClose={() => setAgDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{agEditMode ? t("agent.edit.title") : t("agent.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              fullWidth required
              label={t("agent.name")}
              value={agForm.guidelineName}
              onChange={(e) => setAgForm({ ...agForm, guidelineName: e.target.value })}
            />
            <TextField
              fullWidth required multiline rows={3}
              label={t("agent.description")}
              value={agForm.guidelineDescription}
              onChange={(e) => setAgForm({ ...agForm, guidelineDescription: e.target.value })}
            />
            <TextField
              fullWidth
              label={t("agent.url")}
              value={agForm.url}
              onChange={(e) => setAgForm({ ...agForm, url: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleAgSave}>
            {agEditMode ? t("common.save") : t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
